import { Controller } from '@hotwired/stimulus';
import {csrfHeaders} from '../services/form.services';

type ModalController = { show: () => void };
type QuickCheckController = { restart: () => void };

function isModalController(value: unknown): value is ModalController {
    return typeof value === 'object'
        && value !== null
        && 'show' in value
        && typeof value.show === 'function';
}

function isQuickCheckController(value: unknown): value is QuickCheckController {
    return typeof value === 'object'
        && value !== null
        && 'restart' in value
        && typeof value.restart === 'function';
}

export class AnalysisResultsController extends Controller {
    declare totalProteinValue: number;
    declare totalCarbsValue: number;
    declare totalFatValue: number;
    declare massUnitValue: string;
    private addFoodModal: HTMLElement | null = null;
    private addFoodForm: HTMLFormElement | null = null;
    private addFoodSubmitButton: HTMLButtonElement | null = null;
    private addFoodFormListener: ((event: Event) => void) | null = null;
    private mutationObserver: MutationObserver | null = null;
    private initialProteinTotal = 0;
    private initialCarbsTotal = 0;
    private initialFatTotal = 0;
    private mealSummarySaving = false;
    private readonly mealSummaryUpdateUrl = '/analysis-results/update-meal-summary';

    static values = {
        totalProtein: Number,
        totalCarbs: Number,
        totalFat: Number,
        massUnit: String,
        mealSummaryMaxLength: Number,
    };

    static targets = [
        'macroChart',
        'chartBar',
        'proteinBar',
        'carbsBar',
        'fatBar',
        'proteinLabel',
        'carbsLabel',
        'fatLabel',
        'itemsContainer',
        'foodItem',
        'mealSummaryDisplay',
        'mealSummaryForm',
        'mealSummaryInput',
        'mealSummaryError',
        'mealSummaryEditButton',
        'mealSummarySaveButton',
        'mealSummaryCounter',
    ];
    declare readonly macroChartTarget: HTMLElement;
    declare readonly chartBarTarget: HTMLElement;
    declare readonly proteinBarTarget: HTMLElement;
    declare readonly carbsBarTarget: HTMLElement;
    declare readonly fatBarTarget: HTMLElement;
    declare readonly proteinLabelTarget: HTMLElement;
    declare readonly carbsLabelTarget: HTMLElement;
    declare readonly fatLabelTarget: HTMLElement;
    declare readonly itemsContainerTarget: HTMLElement;
    declare readonly hasItemsContainerTarget: boolean;
    declare readonly foodItemTargets: HTMLElement[];
    declare readonly hasMealSummaryDisplayTarget: boolean;
    declare readonly mealSummaryDisplayTarget: HTMLElement;
    declare readonly hasMealSummaryFormTarget: boolean;
    declare readonly mealSummaryFormTarget: HTMLFormElement;
    declare readonly hasMealSummaryInputTarget: boolean;
    declare readonly mealSummaryInputTarget: HTMLInputElement;
    declare readonly hasMealSummaryErrorTarget: boolean;
    declare readonly mealSummaryErrorTarget: HTMLElement;
    declare readonly hasMealSummaryEditButtonTarget: boolean;
    declare readonly mealSummaryEditButtonTarget: HTMLButtonElement;
    declare readonly hasMealSummarySaveButtonTarget: boolean;
    declare readonly mealSummarySaveButtonTarget: HTMLButtonElement;
    declare readonly hasMealSummaryCounterTarget: boolean;
    declare readonly mealSummaryCounterTarget: HTMLElement;
    declare readonly hasMassUnitValue: boolean;
    declare readonly hasMealSummaryMaxLengthValue: boolean;
    declare readonly mealSummaryMaxLengthValue: number;

    private get mealSummaryLimit(): number {
        return this.hasMealSummaryMaxLengthValue ? this.mealSummaryMaxLengthValue : 50;
    }

    private get massUnit(): string {
        return this.hasMassUnitValue ? this.massUnitValue : 'g';
    }

    connect(): void {
        this.initialProteinTotal = this.totalProteinValue;
        this.initialCarbsTotal = this.totalCarbsValue;
        this.initialFatTotal = this.totalFatValue;
        this.updateTotalsFromItems();
        this.observeItems();
        this.renderStackedMacroChart();
        this.cacheAddFoodElements();
        this.registerAddFoodFormListener();
        this.updateMealSummaryCounter(this.mealSummaryDisplayTarget?.textContent ?? '');
        this.applyDiaryTimeDefaults();
    }

    disconnect(): void {
        this.removeAddFoodFormListener();
        this.mutationObserver?.disconnect();
        this.mutationObserver = null;
    }

    addFoodItem(): void {
        const modal = this.addFoodModal ?? document.getElementById('add-food-item-modal');
        if (!modal) {
            return;
        }

        this.addFoodForm?.reset();

        const modalController = this.application.getControllerForElementAndIdentifier(modal, 'modal');
        if (isModalController(modalController)) {
            modalController.show();
        }
    }

    private cacheAddFoodElements(): void {
        this.addFoodModal = document.getElementById('add-food-item-modal');
        this.addFoodForm = document.getElementById('add-food-item-form') as HTMLFormElement | null;
        this.addFoodSubmitButton = document.getElementById('add-food-item-submit') as HTMLButtonElement | null;
    }

    private registerAddFoodFormListener(): void {
        if (!this.addFoodForm || this.addFoodFormListener) {
            return;
        }

        this.addFoodFormListener = this.handleAddFoodItemSubmit.bind(this);
        this.addFoodForm.addEventListener('submit', this.addFoodFormListener);
    }

    private removeAddFoodFormListener(): void {
        if (this.addFoodForm && this.addFoodFormListener) {
            this.addFoodForm.removeEventListener('submit', this.addFoodFormListener);
        }
        this.addFoodFormListener = null;
    }

    private handleAddFoodItemSubmit(event: Event): void {
        if (this.addFoodSubmitButton) {
            const button = this.addFoodSubmitButton;
            button.dataset.turboResetDisabled = 'true';
            button.disabled = true;
            button.dataset.originalText = button.dataset.originalText || button.textContent || '';
            button.textContent = 'Analyzing…';
            button.classList.add('opacity-75', 'cursor-not-allowed');
        }

        // Allow the form submission to continue for server-side processing
        void event;
    }

    handleInlineAiUpdateSubmit(event: Event): void {
        const form = event.currentTarget as HTMLFormElement | null;
        if (!form) {
            return;
        }

        const button = form.querySelector('[data-analysis-results-inline-update-button]') as HTMLButtonElement | null;
        const label = form.querySelector('[data-analysis-results-inline-update-label]') as HTMLElement | null;
        const spinner = form.querySelector('[data-analysis-results-inline-update-spinner]') as HTMLElement | null;

        if (!button) {
            return;
        }

        button.disabled = true;
        button.dataset.turboResetDisabled = 'true';
        button.classList.add('opacity-75', 'cursor-not-allowed');
        if (label) {
            label.dataset.turboResetLabel = 'true';
        }
        if (spinner) {
            spinner.dataset.turboResetLoading = 'true';
        }
        label?.classList.add('hidden');
        spinner?.classList.remove('hidden');
        spinner?.classList.add('inline-flex');
    }

    markDiaryTimeEdited(event: Event): void {
        const input = event.currentTarget as HTMLInputElement | null;
        if (input) {
            input.dataset.userEdited = 'true';
        }
    }

    syncDiaryTimeBeforeSubmit(event: Event): void {
        const form = event.currentTarget as HTMLFormElement | null;
        if (!form) {
            return;
        }

        this.applyDiaryTimeDefaultForForm(form);
    }

    private observeItems(): void {
        if (!this.hasItemsContainerTarget || this.mutationObserver) {
            return;
        }

        this.mutationObserver = new MutationObserver(() => {
            this.updateTotalsFromItems();
            this.renderStackedMacroChart();
        });

        this.mutationObserver.observe(this.itemsContainerTarget, { childList: true });
    }

    private applyDiaryTimeDefaults(): void {
        const forms = this.element.querySelectorAll<HTMLFormElement>('[data-analysis-results-diary-time-form]');
        for (const form of forms) {
            this.applyDiaryTimeDefaultForForm(form);
        }
    }

    private applyDiaryTimeDefaultForForm(form: HTMLFormElement): void {
        const timeInput = form.querySelector<HTMLInputElement>('input[name="eaten_at"]');
        const dateInput = form.querySelector<HTMLInputElement>('input[name="date"]');
        if (!timeInput) {
            return;
        }

        const serverDefault = timeInput.dataset.serverDefaultValue ?? '';
        const shouldUseLocalDefault = timeInput.dataset.userEdited !== 'true'
            && (timeInput.value.trim() === '' || timeInput.value === serverDefault);

        if (shouldUseLocalDefault) {
            timeInput.value = this.formatDateTimeLocal(new Date());
        }

        if (dateInput && timeInput.value) {
            dateInput.value = timeInput.value.slice(0, 10);
        }
    }

    private formatDateTimeLocal(date: Date): string {
        const pad = (value: number): string => value.toString().padStart(2, '0');
        return [
            date.getFullYear(),
            pad(date.getMonth() + 1),
            pad(date.getDate()),
        ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    private updateTotalsFromItems(): void {
        if (this.foodItemTargets.length === 0) {
            this.totalProteinValue = this.initialProteinTotal;
            this.totalCarbsValue = this.initialCarbsTotal;
            this.totalFatValue = this.initialFatTotal;
            return;
        }

        let readableMacroRows = 0;
        const totals = this.foodItemTargets.reduce(
            (acc, el) => {
                const protein = this.parseMacroValue(el.dataset.protein);
                const carbs = this.parseMacroValue(el.dataset.carbs);
                const fat = this.parseMacroValue(el.dataset.fat);

                if (protein !== null || carbs !== null || fat !== null) {
                    readableMacroRows += 1;
                }

                acc.protein += protein ?? 0;
                acc.carbs += carbs ?? 0;
                acc.fat += fat ?? 0;
                return acc;
            },
            { protein: 0, carbs: 0, fat: 0 },
        );

        if (readableMacroRows === 0 && this.initialProteinTotal + this.initialCarbsTotal + this.initialFatTotal > 0) {
            this.totalProteinValue = this.initialProteinTotal;
            this.totalCarbsValue = this.initialCarbsTotal;
            this.totalFatValue = this.initialFatTotal;
            return;
        }

        this.totalProteinValue = totals.protein;
        this.totalCarbsValue = totals.carbs;
        this.totalFatValue = totals.fat;
    }

    private parseMacroValue(value?: string): number | null {
        if (!value) {
            return null;
        }

        const normalized = value.replace(/[^\d.-]/g, '');
        if (!normalized) {
            return null;
        }

        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }

    rerunAnalysis(): void {
        // Open the re-analyse modal
        const modal = document.getElementById('reanalyse-modal');
        if (modal) {
            const modalController = this.application.getControllerForElementAndIdentifier(modal, 'modal');
            if (isModalController(modalController)) {
                modalController.show();
            }
        }
    }

    showAiEstimatedNutritionDisclaimer(): void {
        const modal = document.getElementById('ai-estimated-nutrition-modal');
        if (!modal) {
            return;
        }

        const modalController = this.application.getControllerForElementAndIdentifier(modal, 'modal');
        if (isModalController(modalController)) {
            modalController.show();
        }
    }

    showQuickCheck(): void {
        const modal = document.getElementById('quick-check-modal');
        if (!modal) {
            return;
        }

        const modalController = this.application.getControllerForElementAndIdentifier(modal, 'modal');
        const quickCheckController = this.application.getControllerForElementAndIdentifier(modal, 'quick-check');
        if (isQuickCheckController(quickCheckController)) {
            quickCheckController.restart();
        }
        if (isModalController(modalController)) {
            modalController.show();
        }
    }

    startMealSummaryEdit(): void {
        if (!this.hasMealSummaryFormTarget || !this.hasMealSummaryDisplayTarget) {
            return;
        }

        if (this.hasMealSummaryInputTarget) {
            const currentText = this.mealSummaryDisplayTarget.textContent?.trim() ?? '';
            this.mealSummaryInputTarget.value = currentText;
            this.mealSummaryInputTarget.focus();
            this.mealSummaryInputTarget.select();
            this.updateMealSummaryCounter(currentText);
        }

        this.clearMealSummaryError();
        this.toggleMealSummaryEdit(true);
    }

    cancelMealSummaryEdit(): void {
        if (!this.hasMealSummaryFormTarget || !this.hasMealSummaryDisplayTarget) {
            return;
        }

        if (this.mealSummarySaving) {
            return;
        }

        this.clearMealSummaryError();

        if (this.hasMealSummaryInputTarget) {
            const currentText = this.mealSummaryDisplayTarget.textContent?.trim() ?? '';
            this.mealSummaryInputTarget.value = currentText;
            this.updateMealSummaryCounter(currentText);
        }

        this.toggleMealSummaryEdit(false);
    }

    handleMealSummaryInput(event: Event): void {
        const target = event.target as HTMLInputElement | undefined;
        this.updateMealSummaryCounter(target?.value ?? '');
    }

    async submitMealSummary(event: Event): Promise<void> {
        event.preventDefault();
        if (!this.hasMealSummaryInputTarget) {
            return;
        }

        const newSummary = this.mealSummaryInputTarget.value.trim();
        if (newSummary.length === 0) {
            this.showMealSummaryError('Please provide a short title for this meal.');
            return;
        }

        if (newSummary.length > this.mealSummaryLimit) {
            this.showMealSummaryError(`Meal title must be ${this.mealSummaryLimit} characters or fewer.`);
            return;
        }

        this.clearMealSummaryError();
        this.setMealSummarySaving(true);

        try {
            const response = await fetch(this.mealSummaryUpdateUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    ...csrfHeaders(),
                },
                body: JSON.stringify({ meal_summary: newSummary }),
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success) {
                const message = data?.message || 'Unable to save your changes right now. Please try again.';
                throw new Error(message);
            }

            const updatedText = (data.meal_summary as string | undefined) ?? newSummary;
            this.updateMealSummaryDisplay(updatedText);
            this.toggleMealSummaryEdit(false);
            this.updateMealSummaryCounter(updatedText);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save your changes right now. Please try again.';
            this.showMealSummaryError(message);
        } finally {
            this.setMealSummarySaving(false);
        }
    }

    private toggleMealSummaryEdit(editing: boolean): void {
        if (this.hasMealSummaryDisplayTarget) {
            this.mealSummaryDisplayTarget.classList.toggle('hidden', editing);
        }
        if (this.hasMealSummaryFormTarget) {
            this.mealSummaryFormTarget.classList.toggle('hidden', !editing);
        }
        if (this.hasMealSummaryEditButtonTarget) {
            this.mealSummaryEditButtonTarget.classList.toggle('hidden', editing);
        }
    }

    private setMealSummarySaving(isSaving: boolean): void {
        this.mealSummarySaving = isSaving;
        if (this.hasMealSummarySaveButtonTarget) {
            this.mealSummarySaveButtonTarget.disabled = isSaving;
            this.mealSummarySaveButtonTarget.textContent = isSaving ? 'Saving…' : 'Save';
        }
    }

    private showMealSummaryError(message: string): void {
        if (!this.hasMealSummaryErrorTarget) {
            return;
        }

        this.mealSummaryErrorTarget.textContent = message;
        this.mealSummaryErrorTarget.classList.remove('hidden');
    }

    private clearMealSummaryError(): void {
        if (!this.hasMealSummaryErrorTarget) {
            return;
        }

        this.mealSummaryErrorTarget.textContent = '';
        this.mealSummaryErrorTarget.classList.add('hidden');
    }

    private updateMealSummaryDisplay(text: string): void {
        if (!this.hasMealSummaryDisplayTarget) {
            return;
        }

        this.mealSummaryDisplayTarget.textContent = text;
    }

    private updateMealSummaryCounter(value: string | undefined): void {
        if (!this.hasMealSummaryCounterTarget) {
            return;
        }

        const safeValue = value?.trim() ?? '';
        const limit = this.mealSummaryLimit;
        this.mealSummaryCounterTarget.textContent = `${Math.min(safeValue.length, limit)}/${limit}`;
    }

    private renderStackedMacroChart(): void {
        const totalMacros = this.totalProteinValue + this.totalCarbsValue + this.totalFatValue;

        if (totalMacros <= 0) {
            // Update bar widths to 0%
            this.proteinBarTarget.style.width = '0%';
            this.carbsBarTarget.style.width = '0%';
            this.fatBarTarget.style.width = '0%';

            // Update labels to show no data
            this.proteinLabelTarget.textContent = '0%';
            this.carbsLabelTarget.textContent = '0%';
            this.fatLabelTarget.textContent = '0%';
            return;
        }

        const proteinPercent = Math.round((this.totalProteinValue / totalMacros) * 100 * 10) / 10;
        const carbsPercent = Math.round((this.totalCarbsValue / totalMacros) * 100 * 10) / 10;
        const fatPercent = Math.round((this.totalFatValue / totalMacros) * 100 * 10) / 10;
        const formatPercent = (percent: number): string => Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(1)}%`;
        const chartLabel = (percent: number): string => percent >= 8 ? formatPercent(percent) : '';

        // Update individual bar widths and content
        this.proteinBarTarget.style.width = `${proteinPercent}%`;
        this.proteinBarTarget.title = `Protein: ${this.totalProteinValue.toFixed(1)}${this.massUnit} (${proteinPercent}%)`;
        this.proteinBarTarget.textContent = chartLabel(proteinPercent);

        this.carbsBarTarget.style.width = `${carbsPercent}%`;
        this.carbsBarTarget.title = `Carbs: ${this.totalCarbsValue.toFixed(1)}${this.massUnit} (${carbsPercent}%)`;
        this.carbsBarTarget.textContent = chartLabel(carbsPercent);

        this.fatBarTarget.style.width = `${fatPercent}%`;
        this.fatBarTarget.title = `Fat: ${this.totalFatValue.toFixed(1)}${this.massUnit} (${fatPercent}%)`;
        this.fatBarTarget.textContent = chartLabel(fatPercent);

        // Update the legend labels
        this.proteinLabelTarget.textContent = formatPercent(proteinPercent);
        this.carbsLabelTarget.textContent = formatPercent(carbsPercent);
        this.fatLabelTarget.textContent = formatPercent(fatPercent);
    }
}
